import { inject, Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { startAuthentication } from '@simplewebauthn/browser';
import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/types';
import {
  adaptApiError,
  createAsyncEffect,
  DialogService,
  DISABLE_ERROR_INTERCEPTOR,
  WINDOW,
} from '@hglobal/common';
import {
  AccessData,
  Enable2FAData,
  LocalStorageAnalyticsModel,
  PersonalInfo,
  SigninWithCodeData,
} from '@pt/models';
import { environment } from '@pt/environment';
import { NgxPermissionsService } from 'ngx-permissions';
import { Store } from '@ngrx/store';
import { RootState, PreferencesStore } from '@pt/store';
import { AnalyticsService, ExtendedRouter, ToastI18nService } from '@pt/services';
import { TextFormaterHelper } from '@pt/helpers';
import * as LOCAL_STORAGE_KEYS from '@pt/constants';
import { FLOATING_BUTTON_LOCATION_STORAGE_KEY, FLOATING_IS_TWU_USER_STORAGE_KEY } from '@pt/constants';

import {
  AUTH_TOKEN_KEY,
  GOOGLE_JWT_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from '../auth.constants';
import { AuthService } from '../auth.service';
import { CodesComponent } from '../components/codes/codes.component';
import { TfaComponent } from '../components/tfa/tfa.component';
import { TfaEnableComponent } from '../components/tfa-enable/tfa-enable.component';
import { AuthActions } from './auth.actions';
import { AuthSelectors } from './auth.selectors';

@Injectable()
export class AuthEffects {
  readonly #router = inject(ExtendedRouter);
  readonly #preferencesStore = inject(PreferencesStore);

  /**
   * Effect is used to send analytics event when user is logging in
   */
  sendAftReq$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.signIn.action),
      tap(({ payload: { token } }) => this._analytics?.sendLoginEvent(token)),
    ),
    { dispatch: false },
  );

  signIn$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.signIn.action),
    switchMap(({ payload: { token }, initiator }) => createAsyncEffect(
      this.signIn(token),
      AuthActions.signIn,
      initiator,
      true,
    )),
  ));

  onSignInSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.signIn.succeededAction),
    tap(({ payload: { token } }) => this._window.localStorage.setItem(GOOGLE_JWT_TOKEN_KEY, token)),
    switchMap(({ payload }) => {
      const step = { token: payload.token, step: '', accessData: null as AccessData | null };
      if (payload.is2faEnabled) {
        return this.verifyFingerprint(payload.token)
          .pipe(
            map(accessData => ({
              ...step, accessData, step: accessData ? 'authenticated' : 'tfa',
            })),
            catchError(() => of({ ...step, step: 'tfa' })),
          );
      } else {
        return of({ ...step, step: 'enableTfa' });
      }
    }),
    switchMap(({ token, step, accessData }) => {
      const panelClass = environment.shortName === 'pt'
        ? 'pu-tfa__hexagonal-background-panel'
        : 'pu-overlay-panel';
      switch (step) {
        case 'enableTfa':
          return [AuthActions.enable2fa.action({ payload: { token } })];

        case 'tfa':
          this._dialog.open(TfaComponent, { token }, { panelClass });
          return EMPTY;

        default:
          return [AuthActions.updateAccessData(accessData), AuthActions.navigateAfter()];
      }
    }),
  ));

  onSignInFailed$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.signIn.failedAction),
    switchMap(({ error }) => {
      if (error.code === 'user_is_not_found' || error.code === 'user_is_not_active') {
        this.#router.navigateByUrl('/user-not-found');
      }

      this._toast.showError(error.message);

      return [];
    }),
  ));

  enable2fa$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.enable2fa.action),
    switchMap(({ payload: { token }, initiator }) => createAsyncEffect(
      this.enable2fa(token),
      AuthActions.enable2fa,
      initiator,
      true,
    )),
  ));

  onEnable2faSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.enable2fa.succeededAction),
      tap(({ payload }) => {
        const token = this._window.localStorage.getItem(GOOGLE_JWT_TOKEN_KEY);
        const panelClass = environment.shortName === 'pt'
          ? 'pu-tfa__hexagonal-background-panel'
          : 'pu-overlay-panel';
        this._dialog.open(TfaEnableComponent, { ...payload, token }, { panelClass });
      }),
    ),
    { dispatch: false },
  );

  remove2fa$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.remove2fa.action),
    switchMap(() => createAsyncEffect(this.remove2fa(), AuthActions.remove2fa)),
  ));

  onRemove2faSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.remove2fa.succeededAction),
      tap(() => this._auth.logout()),
    ),
    { dispatch: false },
  );

  verifyCode$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.verifyCode.action),
    switchMap(({ payload: { token, code }, initiator }) => createAsyncEffect(this.verifyCode(
      token,
      code,
    ), AuthActions.verifyCode, initiator, true)),
  ));

  onVerifyCodeSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.verifyCode.succeededAction),
    concatLatestFrom(() => [this._store.select(AuthSelectors.selectIs2faEnabled)]),
    switchMap(([{ payload }, is2faEnabled]) => {
      if (is2faEnabled) {
        return [AuthActions.updateAccessData(payload), AuthActions.navigateAfter()];
      }

      const panelClass = environment.shortName === 'pt'
        ? 'pu-tfa__hexagonal-background-panel'
        : 'pu-overlay-panel';

      this._dialog.open(CodesComponent, {}, { panelClass });

      return [AuthActions.updateAccessData(payload)];
    }),
  ));

  onVerifyCodeFailed$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.verifyCode.failedAction),
      filter(({ error }) => error.code === 'throttled'),
      tap(({ error }) => {
        this._toast.showError(`error.${error.code}`);
      }),
    ),
    { dispatch: false },
  );

  resetCode$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.resetCode.action),
    switchMap(({ payload: { token, code }, initiator }) => createAsyncEffect(this.resetCode(
      token,
      code,
    ), AuthActions.resetCode, initiator, true)),
  ));

  onResetCodeSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.resetCode.succeededAction),
    switchMap(({ payload }) => [
      AuthActions.updateAccessData(payload),
      AuthActions.navigateAfter(),
    ]),
  ));

  onResetCodeFailed$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.resetCode.failedAction),
      filter(({ error }) => error.code === 'throttled'),
      tap(({ error }) => {
        this._toast.showError(`error.${error.code}`);
      }),
    ),
    { dispatch: false },
  );

  navigateAfter$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.navigateAfter),
    tap(() => {
      const win = this._window as Window;
      const params = new URLSearchParams(win.location.search);
      const encodedUrl = params.get('url');
      let url = encodedUrl ? decodeURIComponent(encodedUrl) : null;

      if (url) {
        const target = new URL(url, win.location.origin);
        const isAbsoluteHttp = target.protocol === 'http:' || target.protocol === 'https:';
        const lms = new URL(environment.lmsHost);
        const httpsOk = lms.protocol === 'https:' ? target.protocol === 'https:' : true;

        if (isAbsoluteHttp && httpsOk && target.origin === lms.origin) {
          win.location.replace(target.href);
          return;
        }

        if (this.#router.isExternalUrl(url)) {
          this.#router.navigateToExternalUrl(url);
          return;
        }
      }

      url = url && !this.#router.isSignInUrl(url)
        ? (url[0] === '/' ? url : '/' + url)
        : '/';
      this.#router.navigateByUrl(url);
    }),
  ), { dispatch: false });

  updateAccessData$ = createEffect(
    () => this._actions$.pipe(
      ofType(AuthActions.updateAccessData),
      tap(({ access, refresh, permissions }) => {
        if (access) {
          this._window.localStorage.setItem(AUTH_TOKEN_KEY, access);
        }

        if (refresh) {
          this._window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
        }

        if (permissions?.length) {
          this._permissions.loadPermissions(permissions);
        }
      }),
    ),
    { dispatch: false },
  );

  logout$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.logout),
    switchMap(() => createAsyncEffect(this.logout(), AuthActions.logoutReq)),
    tap(() => this.#router.navigateToSignIn()),
    switchMap(() => {
      this._permissions.flushPermissions();
      this._window.localStorage.removeItem(AUTH_TOKEN_KEY);
      this._window.localStorage.removeItem(REFRESH_TOKEN_KEY);
      this._window.localStorage.removeItem(GOOGLE_JWT_TOKEN_KEY);
      return [AuthActions.setProfile({ profile: null })];
    }),
  ));

  setProfile$ = createEffect(() => this._actions$.pipe(
    ofType(AuthActions.setProfile),
    tap(({ profile }) => {
      if (environment.shortName !== 'aa' && profile) {
        this.#preferencesStore.loadInitialData();
      }
    }),
    tap(({ profile }) => this._setProfileDataToLocalStorage(profile)),
  ), { dispatch: false });

  constructor(
    private _actions$: Actions,
    private _auth: AuthService,
    private _store: Store<RootState>,
    private _http: HttpClient,
    private _permissions: NgxPermissionsService,
    private _toast: ToastI18nService,
    private _dialog: DialogService,
    @Optional() private _analytics: AnalyticsService,
    @Inject(WINDOW) private _window: Window,
  ) {
  }

  signIn(token: string) {
    return this._http
      .post<SigninWithCodeData>(
        environment.apiHost + 'auth/signin/',
        { token },
        { context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true) },
      )
      .pipe(adaptApiError());
  }

  enable2fa(token: string) {
    return this._http
      .post<Enable2FAData>(
        environment.apiHost + 'auth/enable/',
        { token },
        { context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true) },
      )
      .pipe(adaptApiError());
  }

  verifyFingerprint(token: string) {
    const baseUrl = environment.apiHost + 'auth/webauthn/';
    const context = new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true);

    return this._http.post<PublicKeyCredentialRequestOptionsJSON>(
      baseUrl + 'auth/options/', { token, credentials: [] }, { context },
    )
      .pipe(
        switchMap(options => (options.allowCredentials?.length
          ? startAuthentication(options)
          : of(null))),
        switchMap(credential => (credential
          ? this._http.post<AccessData>(
            baseUrl + 'auth/verify/', { token, credential }, { context },
          )
          : of(null))),
      );
  }

  verifyCode(token: string, code: string) {
    return this._http
      .post<AccessData>(
        environment.apiHost + 'auth/verify/',
        { token, code },
        { context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true) },
      )
      .pipe(adaptApiError());
  }

  resetCode(token: string, code: string) {
    return this._http
      .post<AccessData>(
        environment.apiHost + 'auth/reset/',
        { token, code },
        { context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true) },
      )
      .pipe(adaptApiError());
  }

  remove2fa(): Observable<void> {
    return this._http.delete<void>(environment.apiHost + 'auth/remove/');
  }

  logout(): Observable<void> {
    return this._http.post<void>(
      environment.apiHost + 'auth/logout/',
      {},
      { context: new HttpContext().set(DISABLE_ERROR_INTERCEPTOR, true) },
    );
  }

  private _setProfileDataToLocalStorage(profile: PersonalInfo): void {
    if (!profile) {
      return;
    }

    this._window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.FLOATING_BUTTON_LOCATION_STORAGE_KEY,
      profile.location,
    );
    const isTWUUser = profile.staffType === 'Admin' && profile.staffProject === 'StafCS';
    this._window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.FLOATING_IS_TWU_USER_STORAGE_KEY,
      JSON.stringify(isTWUUser),
    );

    if (!environment.googleAnalyticsId) {
      return;
    }

    const authToken = window.localStorage.getItem(AUTH_TOKEN_KEY);
    const authData = this._auth.decodeJwt(authToken);
    const authUserRoleGroups = authData?.['groups'] as string[];
    const user_role = authUserRoleGroups?.length ? this._getUserRole(authUserRoleGroups) : '';

    const preparePayload: LocalStorageAnalyticsModel = {
      user_id: profile.id,
      user_role,

      [LOCAL_STORAGE_KEYS.QUESTS_IN_PROGRESS_STORAGE_KEY]: 0, // ?
      [LOCAL_STORAGE_KEYS.QUESTS_IN_REVIEW_STORAGE_KEY]: 0, // ?
      [LOCAL_STORAGE_KEYS.QUESTS_COMPLETED_STORAGE_KEY]: 0, // ?
      [LOCAL_STORAGE_KEYS.QUESTS_REJECTED_STORAGE_KEY]: 0, // ?
      [LOCAL_STORAGE_KEYS.QUESTS_CANCELED_STORAGE_KEY]: 0, // ?
      [LOCAL_STORAGE_KEYS.SHOP_LOCATION_STORAGE_KEY]:
        TextFormaterHelper.transformToSnakeCase(profile.location),
      [LOCAL_STORAGE_KEYS.THEME_STORAGE_KEY]: this._window.localStorage.getItem(
        LOCAL_STORAGE_KEYS.UI_IS_DARK_THEME_KEY,
      ) === 'true'
        ? 'dark'
        : 'light',
      [LOCAL_STORAGE_KEYS.COINS_BALANCE_STORAGE_KEY]: profile.balance,

      office_location: TextFormaterHelper.transformToSnakeCase(profile.location),
      user_team: TextFormaterHelper.transformToSnakeCase(profile.team),
      user_position: TextFormaterHelper.transformToSnakeCase(profile.position),
      user_function: TextFormaterHelper.transformToSnakeCase(profile.function),
      user_gender: TextFormaterHelper.transformToSnakeCase(profile.gender),
      user_age: `${profile.age}`,
      user_status: TextFormaterHelper.transformToSnakeCase(profile.status),
      user_work_format: TextFormaterHelper.transformToSnakeCase(profile.workFormat),
      user_segment: TextFormaterHelper.transformToSnakeCase(profile.segment),
    };

    Object.entries(preparePayload).forEach(([key, value]) => {
      this._window.localStorage.setItem(key, value);
    });
  }

  private _getUserRole(roles: string[]): string {
    const extractedRoles = roles.map(role => role.split('.').slice(-2, -1)[0]);

    return extractedRoles.find(role => role.includes('admin'))
      || extractedRoles.find(role => role !== 'user')
      || 'user';
  }
}
